<template>
  <div class="sci-page-wrapper" style="font-family: 'Inter', sans-serif;">
    <div class="max-w-4xl mx-auto space-y-8">
      
      <!-- HEADER -->
      <div class="sci-page-header pb-4">
        <div>
          <h1 class="sci-page-title-only">Configuração de E-mail</h1>
          <p class="text-sm text-slate-500">Gerencie as credenciais de SMTP para envio de recuperação de senha</p>
        </div>
      </div>

      <!-- FORMULÁRIO DE CONFIGURAÇÃO -->
      <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
          <div class="px-6 py-4 flex items-center gap-3" style="background-color: #333333;">
              <v-icon color="orange" small>mdi-email-cog-outline</v-icon>
              <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">
                  SERVIDOR SMTP
              </h2>
          </div>
          
          <div class="p-8">
              <v-form ref="form" v-model="valid">
                <div class="grid grid-cols-1 md:grid-cols-12 gap-6">
                  
                  <!-- Host -->
                  <div class="md:col-span-9">
                    <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 ml-1">Servidor (Host)</label>
                    <v-text-field
                      v-model="emailData.smtp_host"
                      placeholder="Ex: smtp.gmail.com"
                      outlined
                      dense
                      hide-details
                      class="custom-input"
                    />
                  </div>

                  <!-- Port -->
                  <div class="md:col-span-3">
                    <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 ml-1">Porta</label>
                    <v-text-field
                      v-model="emailData.smtp_port"
                      type="number"
                      placeholder="587"
                      outlined
                      dense
                      hide-details
                      class="custom-input"
                    />
                  </div>

                  <!-- User -->
                  <div class="md:col-span-6">
                    <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 ml-1">Usuário / E-mail Autenticação</label>
                    <v-text-field
                      v-model="emailData.smtp_user"
                      placeholder="usuario@dominio.com"
                      outlined
                      dense
                      hide-details
                      class="custom-input"
                    />
                  </div>

                  <!-- Password -->
                  <div class="md:col-span-6">
                    <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 ml-1">Senha Autenticação</label>
                    <v-text-field
                      v-model="emailData.smtp_password"
                      :type="showPassword ? 'text' : 'password'"
                      placeholder="********"
                      outlined
                      dense
                      hide-details
                      class="custom-input"
                      :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                      @click:append="showPassword = !showPassword"
                    />
                  </div>

                  <div class="md:col-span-12">
                    <v-divider class="my-4"></v-divider>
                    <h3 class="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-6">Remetente</h3>
                  </div>

                  <!-- From Name -->
                  <div class="md:col-span-6">
                    <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 ml-1">Nome de Exibição</label>
                    <v-text-field
                      v-model="emailData.email_from_name"
                      placeholder="Ex: CSI Frota - Notificações"
                      outlined
                      dense
                      hide-details
                      class="custom-input"
                    />
                  </div>

                  <!-- From Address -->
                  <div class="md:col-span-6">
                    <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 ml-1">E-mail de Origem</label>
                    <v-text-field
                      v-model="emailData.email_from_address"
                      placeholder="nao-responda@dominio.com"
                      outlined
                      dense
                      hide-details
                      class="custom-input"
                    />
                  </div>

                  <!-- Actions -->
                  <div class="md:col-span-12 flex flex-col md:flex-row justify-between items-center gap-4 mt-8 pt-6 border-t border-slate-100">
                    <button 
                      type="button"
                      @click="testEmailModal = true"
                      class="bg-slate-100 hover:bg-slate-200 text-slate-600 font-bold text-[10px] px-6 py-3 rounded transition-all uppercase flex items-center gap-2"
                    >
                      <v-icon small color="inherit">mdi-email-check-outline</v-icon>
                      Enviar E-mail de Teste
                    </button>

                    <button 
                      type="button"
                      @click="saveSettings"
                      :disabled="loader"
                      class="bg-orange-500 hover:bg-orange-600 text-white font-bold text-[10px] px-10 py-3 rounded shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all uppercase flex items-center gap-2"
                    >
                      <v-icon small color="white">mdi-content-save-outline</v-icon>
                      <span v-if="loader" class="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full mr-2"></span>
                      Salvar Configurações
                    </button>
                  </div>
                </div>
              </v-form>
          </div>
      </section>
    </div>

    <!-- Test Email Modal -->
    <v-dialog v-model="testEmailModal" max-width="400">
      <v-card class="pa-4 rounded-xl">
        <v-card-title class="text-h6 font-weight-bold grey--text text--darken-3 pb-2">
          Testar Envio
        </v-card-title>
        <v-card-text>
          <p class="text-body-2 grey--text text--darken-1 mb-4">
            Informe um endereço de e-mail para receber a mensagem de teste.
          </p>
          <div class="input-group">
            <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 ml-1">Para:</label>
            <v-text-field
              v-model="testEmailTo"
              placeholder="seu-email@exemplo.com"
              outlined
              dense
              hide-details
              class="custom-input"
            />
          </div>
        </v-card-text>
        <v-card-actions class="px-6 pb-6">
          <v-btn text class="text-capitalize" @click="testEmailModal = false">Cancelar</v-btn>
          <v-spacer></v-spacer>
          <v-btn 
            color="#FF6A2A" 
            class="white--text text-capitalize px-6" 
            elevation="0"
            rounded
            :loading="testLoader"
            @click="sendTestEmail"
            :disabled="!testEmailTo"
          >
            Enviar Teste
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Feedback -->
    <v-snackbar multi-line :color="snack.color" v-model="snack.show" transition="fade-transition" elevation="24">
      <div class="flex items-center gap-3">
        <v-icon color="white">{{ snack.color === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
        <span class="font-bold text-xs uppercase tracking-wider">{{ snack.message }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn dark text v-bind="attrs" @click="snack.show = false">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
export default {
  data() {
    return {
      valid: false,
      loader: false,
      testLoader: false,
      showPassword: false,
      testEmailModal: false,
      testEmailTo: "",
      emailData: {
        smtp_host: "",
        smtp_port: 587,
        smtp_user: "",
        smtp_password: "",
        email_from_name: "",
        email_from_address: ""
      },
      snack: {
        show: false,
        message: "",
        color: ""
      }
    };
  },
  created() {
    this.initialize();
  },
  methods: {
    initialize() {
      this.loader = true;
      this.$store.dispatch("setLoader", true);
      this.$http
        .get(`/settings/email`)
        .then(r => {
          if (r.data) {
            this.emailData = r.data;
          }
          this.loader = false;
          this.$store.dispatch("setLoader", false);
        })
        .catch(() => {
          this.loader = false;
          this.$store.dispatch("setLoader", false);
          this.showSnack("Erro ao carregar configurações", "error");
        });
    },
    saveSettings() {
      this.loader = true;
      this.$store.dispatch("setLoader", true);
      this.$http
        .post(`/settings/email`, this.emailData)
        .then(() => {
          this.loader = false;
          this.$store.dispatch("setLoader", false);
          this.showSnack("Configurações salvas com sucesso!", "success");
        })
        .catch(() => {
          this.loader = false;
          this.$store.dispatch("setLoader", false);
          this.showSnack("Erro ao salvar configurações", "error");
        });
    },
    sendTestEmail() {
      if (!this.testEmailTo) return;
      
      this.testLoader = true;
      this.$http
        .post(`/settings/email/test`, { to: this.testEmailTo })
        .then(r => {
          this.testLoader = false;
          this.testEmailModal = false;
          this.showSnack(r.data.message || "E-mail de teste enviado!", "success");
        })
        .catch(e => {
          this.testLoader = false;
          const msg = e.response && e.response.data && e.response.data.error 
            ? e.response.data.error 
            : "Erro ao enviar e-mail de teste";
          this.showSnack(msg, "error");
        });
    },
    showSnack(message, color) {
      this.snack.message = message;
      this.snack.color = color;
      this.snack.show = true;
    }
  }
};
</script>

<style scoped>
/* Custom Input Override */
.custom-input ::v-deep .v-input__slot {
  background: #f8fafc !important;
  border-radius: 8px !important;
  box-shadow: none !important;
  border: 1px solid #e2e8f0 !important;
  transition: all 0.2s ease;
}
.custom-input ::v-deep fieldset {
  border: none !important;
}
.custom-input.v-input--is-focused ::v-deep .v-input__slot {
  background: white !important;
  border-color: #f97316 !important;
  box-shadow: 0 0 0 4px rgba(249, 115, 22, 0.1) !important;
}
.custom-input ::v-deep input {
  font-size: 0.875rem !important;
  color: #334155 !important;
  padding: 12px !important;
}
</style>
